/* eslint-disable import/no-cycle */
import AssignLocationRoleForm from 'components/common/AssignLocationRoleForm';
import React, { CSSProperties, useEffect, useMemo, useRef } from 'react';
import { GeneralFunction } from 'types/common';
import { ILocation, LocationRole } from 'types/location';
import { IUser } from 'types/user';

import { useTwoColumnsLocationFormProps } from './hooks';

const LocationFormUserAssign: React.FC<{
  users?: IUser[];
  setInputValues: (v: (c: any) => any) => any;
  inputValues: Record<string, string>;
  location?: ILocation;
  style?: CSSProperties;
  minimizeThreshold?: number;
  onRightColumnSearch?: (v: string) => any;
  selectedUsers?: IUser[];
  fetchMoreUser?: GeneralFunction;
  userFetchLoading?: boolean;
}> = ({
  users,
  setInputValues,
  inputValues,
  location,
  style,
  minimizeThreshold,
  onRightColumnSearch,
  selectedUsers,
  fetchMoreUser,
  userFetchLoading,
}) => {
  const { rightColumnMapper, leftColumnMapper } = useTwoColumnsLocationFormProps();

  const usersDataForRightColumn = useMemo(() => users?.filter((u) => !inputValues[u.id]), [users, inputValues]);

  useEffect(() => {
    if (usersDataForRightColumn && usersDataForRightColumn.length < 8) {
      fetchMoreUser?.();
    }
  }, [usersDataForRightColumn]);

  // use for checking if the initial location value has location role as "NONE" from the start
  // to prevent creating new usersLocation Entity in case the user hasn't been granted any rights for the selected location
  const initUsersLocationValuesRef = useRef<Record<string, any>>({});

  useEffect(() => {
    if (location)
      setInputValues(() => {
        const usersLocations = location.usersLocations || [];
        const initialUserLocationRoleValues = usersLocations.reduce((acc, userLoc) => {
          let locationRole = LocationRole.NONE;
          if (userLoc.manageUsersPermission) locationRole = LocationRole.LOCATION_ADMIN;
          else if (userLoc.clientPermission) locationRole = LocationRole.EMPLOYEE;
          acc[userLoc.user?.id] = locationRole;
          return acc;
        }, {} as Record<string, string>);
        const newInputValues = { ...initialUserLocationRoleValues };
        initUsersLocationValuesRef.current = newInputValues;
        return newInputValues;
      });
  }, [location]);

  const onRightClick = (v: IUser) => {
    setInputValues((c) => ({ ...c, [v.id]: LocationRole.EMPLOYEE }));
  };

  const leftColumnData = useMemo(() => selectedUsers?.filter((u) => !!inputValues[u.id]) || [], [
    inputValues,
    selectedUsers,
  ]);

  const onLeftColumnSelectChange = (value: LocationRole, user: IUser) => {
    setInputValues((c) => ({ ...c, [user.id]: value }));
  };

  const onLeftColumnDeleteRow = (user: IUser) => {
    setInputValues((c) => {
      const current = { ...c };
      // check if the initial location value has location role as "NONE" from the start
      // to prevent creating new usersLocation Entity in case the user hasn't been granted any rights for the selected location
      // if (initUsersLocationValuesRef.current[user.id]) return { ...c, [user.id]: LocationRole.NONE };
      delete current[user.id];
      return current;
    });
  };

  // const onRightColumnScroll = () => {};

  return (
    <AssignLocationRoleForm
      style={style}
      minimizeThreshold={minimizeThreshold}
      leftProps={{
        data: leftColumnData,
        dataMapper: leftColumnMapper,
        title: 'Assigned User',
        values: inputValues,
        onChange: onLeftColumnSelectChange,
        onDelete: onLeftColumnDeleteRow,
        loading: userFetchLoading,
      }}
      rightProps={{
        onSearch: onRightColumnSearch,
        data: usersDataForRightColumn,
        dataMapper: rightColumnMapper,
        onClick: onRightClick,
        onScroll: fetchMoreUser,
        loading: userFetchLoading,
      }}
    />
  );
};

export default React.memo(LocationFormUserAssign);
